//@flow
import * as Immutable from 'immutable';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import I18nHelper from 'common/helpers/I18nHelper';
import CountryHelper from 'common/helpers/CountryHelper';
import type {
    EntityImportTable,
    EntityImportRowModelType
} from 'common/models/flow/EntityImportTable';
import type {
    EntitySettings,
    CustomFieldModelType
} from 'common/models/flow/EntitySettings';
import {
    MasterPersonnel,
    AccessTypeEnum
} from 'common/models/internal/MasterPersonnel';
import type { Map } from 'immutable';
import { Team } from 'common/models/internal/Team';
import { TransporterId } from 'common/models/internal/TransporterId';
import { isValidLanguage } from 'common/constants/languageCodes';
import { PersonnelTypeEnum } from 'common/enums/PersonnelEnums';

import { RoleId } from 'common/models/internal/RoleId';
import { type RoleIdModelType } from 'common/models/flow/RoleId';
import { isFieldHidden, isTransporterPersonnelEnabled } from './utils';

type ValidateType = { entity: MasterPersonnel };

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export class PersonnelImportHelper extends EntityImportDataHelper {
    static validateEmailAddress({
        entity
    }: {
        entity: MasterPersonnel
    }): ?string {
        return entity.validateEmail();
    }

    static validateHasAllTeamsAccess({ entity }: ValidateType): ?string {
        return entity.validateHasAllTeamsAccess();
    }

    static validatePhoneNumber({ entity }: ValidateType): ?string {
        return entity.validatePhone();
    }

    static validateTeams({
        entity,
        teams
    }: {
        entity: MasterPersonnel,
        teams: Map<{ [key: string]: Team }>
    }): ?string {
        if (!entity.hasAllTeamsAccess) {
            return EntityImportDataHelper.validateTeamIds({ entity, teams });
        }
    }

    static validatePersonelType({
        entity,
        context
    }: {
        entity: MasterPersonnel
    }): ?string {
        if (entity.personnelType !== context.personnelType) {
            return nsTranslate('entityValidationErrors.toEqual', {
                fieldType: nsTranslate(
                    'masterPersonnelProperties.personnelType'
                ),
                value: nsTranslate(
                    `masterPersonnelProperties.personnelTypes.${context.personnelType}`
                )
            });
        }
    }

    static processPersonnelType = ({
        cellValue
    }: {
        cellValue: ?string
    }): string => {
        if (cellValue) {
            const translatedPersonnelTypes = PersonnelTypeEnum.getOptions({
                translationKey: 'masterPersonnelProperties.personnelTypes'
            });
            const matchedPersonnel = translatedPersonnelTypes.find(
                ({ label }) => label === cellValue
            );
            if (matchedPersonnel) {
                return matchedPersonnel.value;
            }
            return ' ';
        }
        return cellValue;
    };

    static processAccessType = ({
        cellValue
    }: {
        cellValue: ?string
    }): string => {
        if (cellValue) {
            const translatedPersonnelTypes = AccessTypeEnum.getOptions({
                translationKey: 'masterPersonnelProperties.accessTypes'
            });
            const matchedPersonnel = translatedPersonnelTypes.find(
                ({ label }) => label === cellValue
            );
            if (matchedPersonnel) {
                return matchedPersonnel.value;
            }
            return ' '; // HACK for import when user value from access type enum
        }
        return cellValue;
    };

    static getCustomFields(
        entitySettings: EntitySettings
    ): Immutable.List<CustomFieldModelType> {
        return entitySettings?.personnelSettings?.customFields;
    }

    static processEntityRows = (
        {
            rows
        }: {
            rows: Immutable.List<EntityImportRowModelType>
        },
        { getDataForCell }
    ): Immutable.List<MasterPersonnel> => {
        const { entitySettings } = getDataForCell();
        const isTransporterEnabled = isTransporterPersonnelEnabled(
            entitySettings
        );
        return rows.map(_entityRow => {
            let entityRow = _entityRow;
            if (!isTransporterEnabled) {
                entityRow = _entityRow.set(
                    'personnelType',
                    PersonnelTypeEnum.CLIENT
                );
            }
            return entityRow.map((cellValue, headerId) => {
                if (headerId === 'customProperties') {
                    return cellValue.filter(value => value);
                }

                return cellValue;
            });
        });
    };

    static getUploadedEntities = ({
        uploadedTable,
        getDataForCell,
        clientId
    }: {
        uploadedTable: ?EntityImportTable,
        getDataForCell: Object => void,
        clientId: string
    }): Immutable.List<MasterPersonnel> => {
        const personnelsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: PersonnelImportHelper.processEntityRows
        });
        const personnelEntities = Immutable.List(
            personnelsData.map(personnelEntity =>
                MasterPersonnel.fromJS(personnelEntity.toJS())
            )
        );

        return personnelEntities;
    };

    static generateEntityList = (
        personnelsData: Array<*>
    ): Immutable.List<MasterPersonnel> =>
        Immutable.List(
            personnelsData.map(personnelEntity =>
                MasterPersonnel.fromJS(personnelEntity)
            )
        );

    static getDataForCell = ({ clientId, entitySettings }) => {
        return {
            clientId,
            entitySettings
        };
    };

    static validateLanguage({ entity }: { entity: MasterPersonnel }): ?string {
        const { language } = entity;
        if (language && !isValidLanguage(language)) {
            return nsTranslate(
                'masterPersonnelValidations.languageNotSupported',
                { language }
            );
        }
    }

    static validateTimezone({ entity }: { entity: MasterPersonnel }): ?string {
        const { timezone } = entity;
        if (timezone) {
            const allTimezoneOptions = CountryHelper.getAllTimezoneOptions();
            const matchedTimezone = allTimezoneOptions.find(
                option => option.timezone === timezone
            );
            if (!matchedTimezone) {
                return nsTranslate(
                    'masterPersonnelValidations.timezoneNotSupported',
                    { timezone }
                );
            }
        }
    }

    static processRoleId({
        cellValue,
        clientId
    }: {
        cellValue: ?string,
        clientId: string
    }): ?RoleIdModelType {
        return cellValue
            ? {
                  id: cellValue.trim(),
                  clientId
              }
            : null;
    }

    static serializeRoleId(roleId: ?RoleIdModelType): ?RoleId {
        return roleId ? RoleId.fromJS(roleId) : null;
    }

    static processTransporterIds({
        cellValue,
        clientId
    }: {
        cellValue: ?string,
        clientId: string
    }) {
        return cellValue
            ? cellValue
                  .split(',')
                  .filter(id => id && id.trim())
                  .map(id => {
                      return {
                          transporterId: id.trim(),
                          clientId
                      };
                  })
            : [];
    }

    static serializeTransporterIds = (transporterIds: Array): Immutable.List =>
        Immutable.List(transporterIds).map(item => TransporterId.fromJS(item));

    static isFieldHidden({
        property,
        entitySettings,
        context
    }: {
        property: string,
        entitySettings: any,
        context: any
    }): boolean {
        return isFieldHidden(property, {
            personnelType: context.personnelType,
            entitySettings
        });
    }
}

export default PersonnelImportHelper;
